import { useEffect, useState } from 'react'
import { QueryClient, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { updateTransactionDescription } from 'services/fund'
import { MoneyPoolCashin } from 'utilities/helper/moneyPools'

/**
 * Write a saved description onto the matching row of every cached page of the
 * fund's transaction list.
 *
 * Matching needs BOTH the id and the kind: cash-ins and cash-outs are separate
 * tables merged into one list, so the two can share a primary key and matching
 * on `id` alone would re-tag an unrelated transaction.
 */
export function patchCachedDescription(
    queryClient: QueryClient,
    {
        transactionId,
        isCashin,
        tag,
    }: { transactionId: number; isCashin: boolean; tag: string }
) {
    queryClient.setQueriesData<IMoneyPoolTransaction | undefined>(
        'money-pools-transactions',
        (page) => {
            const rows = page?.results?.result
            if (!rows) return page
            let changed = false
            const next = rows.map((row) => {
                const rowIsCashin =
                    row.type?.substring(0, 2) === MoneyPoolCashin
                if (row.id !== transactionId || rowIsCashin !== isCashin) {
                    return row
                }
                changed = true
                return { ...row, tag }
            })
            if (!changed) return page
            return { ...page, results: { ...page!.results, result: next } }
        }
    )
}

/**
 * Inline editing of one transaction's description.
 *
 * The saved value is kept locally as well as pushed into the query cache: the
 * sheet stays mounted over the row it opened from, so re-reading `item.tag`
 * after a save would show the old text until the list refetched behind it.
 * `description` is what the sheet renders, and it is the server's answer.
 */
export default function useEditDescription({
    moneypoolId,
    transactionId,
    isCashin,
    initialTag,
}: {
    moneypoolId: number
    transactionId: number
    isCashin: boolean
    initialTag: string
}) {
    const queryClient = useQueryClient()
    const [description, setDescription] = useState(initialTag ?? '')
    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState(initialTag ?? '')

    // This sheet stays mounted over the row it opened from, so a list refetch
    // hands it a fresh `item` — and therefore a fresh `initialTag` — underneath
    // the manager. Adopt it, unless they are mid-edit, in which case their
    // unsaved text must not be yanked out from under them.
    //
    // Saving therefore has to write the new description into the CACHED row too
    // (see `onSuccess`); otherwise the invalidate-driven refetch arrives with
    // the row as it was before the edit and this effect snaps the sheet back to
    // the old text. That is not theoretical — it is what the UI did.
    useEffect(() => {
        if (isEditing) return
        setDescription(initialTag ?? '')
    }, [initialTag, isEditing])

    const { mutate, isLoading: isSaving } = useMutation({
        mutationFn: (tag: string) =>
            updateTransactionDescription({
                moneypoolId,
                transactionId,
                isCashin,
                tag,
            }),
        onSuccess: (data, tag) => {
            posthog.capture('transaction_description_edited', {
                moneypool_id: moneypoolId,
                is_cashin: isCashin,
                has_hashtag: tag.includes('#'),
                length: tag.length,
            })
            const saved = data?.tag ?? tag
            setDescription(saved)
            setIsEditing(false)
            toast.success('توضیحات ذخیره شد')
            // Patch the cached row FIRST. The list is what feeds this sheet's
            // `item`, so leaving it holding the old text means the refetch below
            // re-renders the sheet with the description the manager just
            // replaced. It also updates the row behind the sheet immediately,
            // rather than after the round trip.
            patchCachedDescription(queryClient, {
                transactionId,
                isCashin,
                tag: saved,
            })
            // …then re-read, because a hashtag filter running over this list
            // may no longer match the row that was just re-tagged.
            queryClient.invalidateQueries('money-pools-transactions')
        },
        onError: () => {
            posthog.capture('transaction_description_edit_failed', {
                moneypool_id: moneypoolId,
                is_cashin: isCashin,
            })
            toast.error('ذخیره‌ی توضیحات انجام نشد')
        },
    })

    const startEditing = () => {
        setDraft(description)
        setIsEditing(true)
    }

    const cancelEditing = () => {
        setDraft(description)
        setIsEditing(false)
    }

    const save = () => {
        const tag = draft.trim()
        if (tag === description.trim()) {
            setIsEditing(false)
            return
        }
        mutate(tag)
    }

    return {
        description,
        isEditing,
        draft,
        setDraft,
        startEditing,
        cancelEditing,
        save,
        isSaving,
    }
}
